import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /** @type {MutationObserver | null} */
  #contentObserver = null;

  /** @type {number | null} */
  #syncTimeout = null;

  /** @type {AbortController | null} */
  #recommendationsAbortController = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.addEventListener('click', this.#handleRecommendationClick);
    this.#observeDrawerContent();

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.removeEventListener('click', this.#handleRecommendationClick);
    this.#historyAbortController?.abort();
    this.#contentObserver?.disconnect();
    this.#recommendationsAbortController?.abort();

    if (this.#syncTimeout !== null) {
      window.clearTimeout(this.#syncTimeout);
      this.#syncTimeout = null;
    }
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #handleCartAdd = (event) => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }

    this.#announceCartCount(event.detail.resource?.item_count);
    this.#queueEnhancementSync(140);
  };

  #handleDialogOpen = () => {
    this.#queueEnhancementSync();
  };

  #handleRecommendationClick = (event) => {
    const button = event.target instanceof Element
      ? event.target.closest('[data-cart-drawer-recommendation-add]')
      : null;

    if (!(button instanceof HTMLButtonElement)) return;

    event.preventDefault();
    const variantId = Number(button.dataset.variantId);
    if (!variantId) return;

    this.#addRecommendationToCart(button, variantId);
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #observeDrawerContent() {
    const inner = this.querySelector('.cart-drawer__inner');
    if (!inner || this.#contentObserver) return;

    this.#contentObserver = new MutationObserver((mutations) => {
      const shouldSync = mutations.some((mutation) =>
        [...mutation.addedNodes, ...mutation.removedNodes].some((node) => {
          if (!(node instanceof Element)) return false;

          return (
            node.matches('cart-items-component, .cart-drawer__items, .cart-drawer__summary, .cart-form') ||
            node.querySelector('cart-items-component, .cart-drawer__items, .cart-drawer__summary, .cart-form')
          );
        }),
      );

      if (shouldSync) {
        this.#queueEnhancementSync(80);
      }
    });

    this.#contentObserver.observe(inner, { childList: true, subtree: true });
  }

  #queueEnhancementSync(delay = 0) {
    if (this.#syncTimeout !== null) {
      window.clearTimeout(this.#syncTimeout);
    }

    this.#syncTimeout = window.setTimeout(() => {
      this.#syncTimeout = null;
      void this.#syncDrawerEnhancements();
    }, delay);
  }

  async #syncDrawerEnhancements() {
    const dialog = /** @type {HTMLDialogElement | undefined} */ (this.refs.dialog);
    if (!dialog) return;

    const shippingBars = dialog.querySelectorAll('[data-cart-drawer-shipping-bar]');
    const recommendationsRoot = dialog.querySelector('[data-cart-drawer-recommendations]');

    if (!shippingBars.length && !recommendationsRoot) return;

    try {
      const cartResponse = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });

      if (!cartResponse.ok) throw new Error('Unable to load cart state');

      const cart = await cartResponse.json();
      this.#updateShippingBars(shippingBars, cart);
      await this.#updateRecommendations(recommendationsRoot, cart);
    } catch (error) {
      if (recommendationsRoot instanceof HTMLElement) {
        recommendationsRoot.hidden = true;
      }
    }
  }

  #updateShippingBars(shippingBars, cart) {
    shippingBars.forEach((bar) => {
      if (!(bar instanceof HTMLElement)) return;

      const currency = cart.currency || bar.dataset.currency || 'EUR';
      const totalPrice = Number(cart.total_price || 0);
      const thresholdMap = {
        EUR: Number(bar.dataset.thresholdEur || 10000),
        USD: Number(bar.dataset.thresholdUsd || 11500),
        GBP: Number(bar.dataset.thresholdGbp || 9000),
        CHF: Number(bar.dataset.thresholdChf || 9500),
      };
      const threshold = thresholdMap[currency] || thresholdMap.EUR;
      const remaining = Math.max(threshold - totalPrice, 0);
      const progress = threshold > 0 ? Math.min(100, (totalPrice / threshold) * 100) : 0;

      let message = this.#formatMessage(this.#decodeHtml(bar.dataset.emptyTemplate || ''), {
        threshold: this.#formatMoney(threshold, currency),
      });
      let state = 'empty';

      if (totalPrice > 0 && remaining > 0) {
        message = this.#formatMessage(this.#decodeHtml(bar.dataset.remainingTemplate || ''), {
          amount: this.#formatMoney(remaining, currency),
        });
        state = 'progress';
      } else if (totalPrice >= threshold && totalPrice > 0) {
        message = this.#decodeHtml(bar.dataset.unlockedMessage || '');
        state = 'success';
      }

      const messageNode = bar.querySelector('[data-cart-drawer-shipping-message]');
      const progressNode = bar.querySelector('[data-cart-drawer-shipping-progress]');

      if (messageNode) {
        messageNode.textContent = message;
      }

      if (progressNode instanceof HTMLElement) {
        progressNode.style.width = `${progress.toFixed(2)}%`;
      }

      bar.dataset.currency = currency;
      bar.dataset.progress = progress.toFixed(2);
      bar.classList.remove(
        'cart-drawer-free-shipping--empty',
        'cart-drawer-free-shipping--progress',
        'cart-drawer-free-shipping--success',
      );
      bar.classList.add(`cart-drawer-free-shipping--${state}`);
    });
  }

  async #updateRecommendations(root, cart) {
    if (!(root instanceof HTMLElement)) return;

    const track = root.querySelector('[data-cart-drawer-recommendations-track]');
    if (!(track instanceof HTMLElement)) return;

    const productIds = Array.isArray(cart.items)
      ? cart.items.map((item) => item.product_id).filter(Boolean)
      : [];
    const baseProductId = productIds[0] || Number(root.dataset.baseProductId || 0);

    if (!baseProductId) {
      root.hidden = true;
      return;
    }

    const requestKey = `${cart.currency || 'EUR'}:${productIds.join(',')}`;
    if (root.dataset.requestKey === requestKey && track.children.length > 0) {
      root.hidden = false;
      return;
    }

    root.hidden = false;
    track.innerHTML = `<p class="cart-drawer-recommendations__status">${this.#escapeHtml(root.dataset.loadingLabel || 'Loading...')}</p>`;

    this.#recommendationsAbortController?.abort();
    this.#recommendationsAbortController = new AbortController();

    try {
      const response = await fetch(
        `${window.Shopify?.routes?.root || '/'}recommendations/products.json?product_id=${baseProductId}&limit=6&intent=related`,
        {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
          signal: this.#recommendationsAbortController.signal,
        },
      );

      if (!response.ok) throw new Error('Unable to load recommendations');

      const payload = await response.json();
      const cartProductIds = new Set(productIds);
      const products = Array.isArray(payload.products) ? payload.products : [];
      const cards = [];

      for (const product of products) {
        if (!product || cartProductIds.has(product.id)) continue;

        const variant = Array.isArray(product.variants)
          ? product.variants.find((item) => item.available) || product.variants[0]
          : null;

        if (!variant?.id) continue;

        const imageUrl = product.featured_image || product.images?.[0] || '';
        const currentPrice = this.#formatMoney(this.#normalizeCents(variant.price ?? product.price), cart.currency);
        const comparePriceCents = this.#normalizeCents(variant.compare_at_price ?? product.compare_at_price);
        const comparePrice = comparePriceCents > this.#normalizeCents(variant.price ?? product.price)
          ? this.#formatMoney(comparePriceCents, cart.currency)
          : '';

        cards.push(`
          <article class="cart-drawer-recommendations__card">
            <a class="cart-drawer-recommendations__media" href="${product.url}" aria-label="${this.#escapeHtml(product.title)}">
              ${imageUrl ? `<img src="${imageUrl}" alt="${this.#escapeHtml(product.title)}" loading="lazy">` : ''}
            </a>
            <div class="cart-drawer-recommendations__content">
              <a class="cart-drawer-recommendations__product-link" href="${product.url}">
                <p class="cart-drawer-recommendations__product-title">${this.#escapeHtml(product.title)}</p>
              </a>
              <div class="cart-drawer-recommendations__price">
                <span>${this.#escapeHtml(currentPrice)}</span>
                ${comparePrice ? `<s>${this.#escapeHtml(comparePrice)}</s>` : ''}
              </div>
              <button
                type="button"
                class="cart-drawer-recommendations__add"
                data-cart-drawer-recommendation-add
                data-variant-id="${variant.id}"
              >
                ${this.#escapeHtml(root.dataset.addLabel || 'Add to cart')}
              </button>
            </div>
          </article>
        `);

        if (cards.length >= 3) break;
      }

      if (!cards.length) {
        root.hidden = true;
        track.innerHTML = '';
        return;
      }

      root.dataset.requestKey = requestKey;
      track.innerHTML = cards.join('');
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;

      const emptyLabel = root.dataset.emptyLabel || '';
      if (emptyLabel) {
        track.innerHTML = `<p class="cart-drawer-recommendations__status">${this.#escapeHtml(emptyLabel)}</p>`;
        root.hidden = false;
      } else {
        root.hidden = true;
      }
    }
  }

  async #addRecommendationToCart(button, variantId) {
    if (button.disabled) return;

    const originalLabel = button.textContent || '';
    button.disabled = true;

    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        credentials: 'same-origin',
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1 }],
        }),
      });

      if (!response.ok) throw new Error('Unable to add recommendation');

      const addedItem = await response.json();
      button.textContent = button.closest('[data-cart-drawer-recommendations]')?.dataset.addedLabel || originalLabel;

      document.dispatchEvent(
        new CustomEvent(CartAddEvent.eventName, {
          bubbles: true,
          detail: { resource: addedItem },
        }),
      );

      this.#queueEnhancementSync(100);
      window.setTimeout(() => {
        button.disabled = false;
        button.textContent = originalLabel;
      }, 1200);
    } catch (error) {
      button.disabled = false;
      button.textContent = originalLabel;
    }
  }

  #formatMessage(template, replacements) {
    return Object.entries(replacements).reduce((message, [key, value]) => {
      return message.replace(`[[${key}]]`, value);
    }, template);
  }

  #formatMoney(cents, currency = 'EUR') {
    const amount = this.#normalizeCents(cents) / 100;

    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency,
        maximumFractionDigits: Number.isInteger(amount) ? 0 : 2,
      }).format(amount);
    } catch (error) {
      return `${currency} ${amount.toFixed(2)}`;
    }
  }

  #normalizeCents(value) {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? Math.round(numericValue) : 0;
  }

  #decodeHtml(value) {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = String(value);
    return textarea.value;
  }

  #escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
